
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, Trash2, Loader2, Lock } from 'lucide-react';
import { addComment, fetchComments, deleteComment, TranscriptComment } from '../services/historyService';
import { auth } from '../services/supabase';

interface CommentPopoverProps {
  transcriptId: string;
  isPaid: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

interface PopoverPosition {
  top: number;
  left: number;
}

const CommentPopover: React.FC<CommentPopoverProps> = ({ transcriptId, isPaid, containerRef }) => {
  const [comments, setComments] = useState<TranscriptComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [selectionOffsets, setSelectionOffsets] = useState<{ start: number; end: number } | null>(null);
  const [popoverPos, setPopoverPos] = useState<PopoverPosition | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const user = auth.currentUser;

  const loadComments = useCallback(async () => {
    if (!transcriptId || !auth.currentUser) return;
    setIsLoading(true);
    try {
      const fetched = await fetchComments(transcriptId);
      setComments(fetched);
    } catch (e) {
      console.error('Failed to fetch comments', e);
    } finally {
      setIsLoading(false);
    }
  }, [transcriptId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        setPopoverPos(null);
        setSelectedText('');
        setSelectionOffsets(null);
        return;
      }

      const text = selection.toString().trim();
      if (!text) return;

      const range = selection.getRangeAt(0);
      const container = containerRef.current;
      if (!container || !container.contains(range.commonAncestorContainer)) {
        setPopoverPos(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      setSelectedText(text);
      setSelectionOffsets({
        start: range.startOffset,
        end: range.endOffset,
      });
      setPopoverPos({
        top: rect.top - containerRect.top - 48,
        left: rect.left - containerRect.left + rect.width / 2,
      });
    };

    document.addEventListener('mouseup', handleSelectionChange);
    return () => document.removeEventListener('mouseup', handleSelectionChange);
  }, [containerRef]);

  // Close popover on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverPos(null);
        setNewComment('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSaveComment = async () => {
    if (!newComment.trim() || !selectionOffsets) return;
    setIsSaving(true);
    try {
      await addComment(transcriptId, {
        transcriptId,
        selectedText,
        startOffset: selectionOffsets.start,
        endOffset: selectionOffsets.end,
        comment: newComment.trim(),
      });
      setNewComment('');
      setPopoverPos(null);
      setSelectedText('');
      await loadComments();
    } catch (e) {
      console.error('Failed to save comment', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(transcriptId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (e) {
      console.error('Failed to delete comment', e);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Inline selection popover */}
      {popoverPos && isPaid && (
        <div
          ref={popoverRef}
          className="absolute z-50 animate-fade-in"
          style={{
            top: popoverPos.top,
            left: popoverPos.left,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="bg-white dark:bg-dark-200 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 p-4 w-72">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={16} className="text-brand-primary" />
              <span className="text-sm font-bold text-base-content dark:text-dark-content">Add Comment</span>
            </div>
            <p className="text-xs text-base-content/50 dark:text-dark-content/50 italic mb-3 truncate">
              "{selectedText}"
            </p>
            <textarea
              autoFocus
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Add your annotation..."
              className="w-full text-sm p-2 rounded-xl border border-base-300 dark:border-dark-300 bg-base-100 dark:bg-dark-100 text-base-content dark:text-dark-content resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 h-20"
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSaveComment();
                if (e.key === 'Escape') { setPopoverPos(null); setNewComment(''); }
              }}
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => { setPopoverPos(null); setNewComment(''); }}
                className="px-3 py-1.5 text-xs rounded-lg border border-base-300 dark:border-dark-300 text-base-content/60 hover:bg-base-200 dark:hover:bg-dark-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveComment}
                disabled={isSaving || !newComment.trim()}
                className="px-3 py-1.5 text-xs rounded-lg bg-brand-primary text-white font-semibold hover:bg-brand-dark disabled:opacity-40 transition-colors flex items-center gap-1"
              >
                {isSaving ? <Loader2 size={12} className="animate-spin" /> : null}
                Save
              </button>
            </div>
          </div>
          {/* Arrow */}
          <div className="w-3 h-3 bg-white dark:bg-dark-200 border-b border-r border-base-300 dark:border-dark-300 rotate-45 mx-auto -mt-1.5" />
        </div>
      )}

      {popoverPos && !isPaid && (
        <div
          ref={popoverRef}
          className="absolute z-50 animate-fade-in"
          style={{ top: popoverPos.top, left: popoverPos.left, transform: 'translateX(-50%)' }}
        >
          <div className="bg-white dark:bg-dark-200 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 p-4 flex items-center gap-3">
            <Lock size={16} className="text-amber-500" />
            <p className="text-sm text-base-content dark:text-dark-content">Comments are a <span className="font-bold text-brand-primary">paid</span> feature.</p>
          </div>
        </div>
      )}

      {/* Comments panel toggle */}
      <button
        onClick={() => setShowPanel(v => !v)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-base-300 dark:border-dark-300 bg-white dark:bg-dark-200 text-sm font-semibold text-base-content/70 hover:text-brand-primary hover:border-brand-primary/50 transition-all shadow-sm"
        title="View comments"
      >
        <MessageSquare size={16} />
        <span>Comments {comments.length > 0 && `(${comments.length})`}</span>
      </button>

      {/* Comments side panel */}
      {showPanel && (
        <div
          ref={panelRef}
          className="fixed right-4 top-24 z-40 w-80 max-h-[70vh] bg-white dark:bg-dark-200 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 flex flex-col animate-fade-in"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-base-200 dark:border-dark-300">
            <div className="flex items-center gap-2">
              <MessageSquare size={16} className="text-brand-primary" />
              <span className="font-bold text-base-content dark:text-dark-content text-sm">Annotations</span>
            </div>
            <button onClick={() => setShowPanel(false)} className="p-1 rounded-lg hover:bg-base-200 dark:hover:bg-dark-300 text-base-content/40 hover:text-base-content transition-colors">
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 size={24} className="animate-spin text-brand-primary" />
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-base-content/40 dark:text-dark-content/40">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No annotations yet.</p>
                {isPaid && <p className="text-xs mt-1">Select text in the transcript to add one.</p>}
              </div>
            ) : (
              comments.map(c => (
                <div key={c.id} className="p-3 rounded-xl bg-base-100 dark:bg-dark-100 border border-base-200 dark:border-dark-300 group">
                  <p className="text-xs italic text-brand-primary/70 mb-1 truncate">"{c.selectedText}"</p>
                  <p className="text-sm text-base-content dark:text-dark-content">{c.comment}</p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-base-content/40">{new Date(c.createdAt).toLocaleDateString()}</span>
                    <button
                      onClick={() => c.id && handleDeleteComment(c.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CommentPopover;
